import { Icon } from '@/modules/Icon';
import { Input } from '@/modules/Input';
import { Modal } from '@/modules/Modal';
import { AlertDialogProps } from '@/shared/types/alertdialog';
import { useState } from 'react';

/**
 * AlertDialog - Componente de diálogo de confirmación/alert
 *
 * Componente de diálogo modal especializado para confirmaciones y alertas.
 * Soporta texto de confirmación requerido, iconos personalizados y múltiples tamaños.
 *
 * @example
 * ```tsx
 * // AlertDialog simple
 * <AlertDialog
 *   isOpen={isOpen}
 *   onClose={() => setIsOpen(false)}
 *   heading="¿Estás seguro?"
 *   subheading="Esta acción no se puede deshacer"
 *   onConfirm={handleConfirm}
 * />
 *
 * // AlertDialog con confirmación de texto
 * <AlertDialog
 *   isOpen={isOpen}
 *   onClose={() => setIsOpen(false)}
 *   heading="Eliminar cuenta"
 *   confirmationText="ELIMINAR"
 *   onConfirm={handleDelete}
 * />
 * ```
 *
 * @see {@link AlertDialogProps} para la lista completa de props
 */
export const AlertDialog = ({
  isOpen,
  onClose,
  label = '',
  heading,
  subheading,
  confirmationText,
  onConfirm,
  icon,
  iconDuotonePrimary,
  iconDuotoneSecondary,
  iconDuotoneOpacityPrimary,
  iconDuotoneOpacitySecondary,
  iconContent,
  iconContentDuotonePrimary,
  iconContentDuotoneSecondary,
  iconContentDuotoneOpacityPrimary,
  iconContentDuotoneOpacitySecondary,
  placeholder = '',
  title,
  successButtonColor = 'destructive',
  successButtonText = 'Confirmar',
  size = 'md',
}: AlertDialogProps) => {
  const [value, setValue] = useState('');

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      onSuccess={onConfirm}
      size={size}
      zIndex={60}
      disableSuccessButton={confirmationText ? value !== confirmationText : false}
      icon={icon}
      iconDuotonePrimary={iconDuotonePrimary}
      iconDuotoneSecondary={iconDuotoneSecondary}
      iconDuotoneOpacityPrimary={iconDuotoneOpacityPrimary}
      iconDuotoneOpacitySecondary={iconDuotoneOpacitySecondary}
      title={title}
      successButtonColor={successButtonColor}
      successButtonText={successButtonText}
    >
      <Modal.Body>
        <div className="ui-flex ui-flex-col ui-items-center ui-gap-4 ui-p-4">
          {iconContent && (
            <Icon
              name={iconContent}
              background
              size="lg"
              color="blue"
              backgroundSize="lg"
              colorDuotonePrimary={iconContentDuotonePrimary}
              colorDuotoneSecondary={iconContentDuotoneSecondary}
              opacityDuotonePrimary={iconContentDuotoneOpacityPrimary}
              opacityDuotoneSecondary={iconContentDuotoneOpacitySecondary}
            />
          )}
          {heading && (
            <h2 className="ui-text-3xl ui-font-semibold ui-text-neutral-700">{heading}</h2>
          )}

          {subheading && <h3 className="ui-text-center ui-text-neutral-500">{subheading}</h3>}

          {confirmationText && (
            <Input
              fullWidth
              label={label}
              value={value}
              onChange={(e) => setValue(e.target.value)}
              placeholder={placeholder}
            />
          )}
        </div>
      </Modal.Body>
    </Modal>
  );
};
